import { useMemo, useState, useEffect } from "react";
import { useUser } from "../../../hooks/useLogin";
import UsersTable from "./UsersTable";
import {
  useBulkCreateUserMutation,
  useDeleteUserMutation,
  useGetUsersQuery,
  useUpdateUserMutation,
} from "../../../redux/reduxQuery/usersApi";
import { useMutationWrapper } from "../../../common/api";
import { DeleteUserModal } from "./DeleteUserModal";
import { BulkAddUsersModal } from "./BulkAddUsersModal";
import { EditUserModal } from "./EditUserModal";
import { ResetUserPasswordModal } from "./ResetUserPasswordModal";

interface IUsers {
  firmId: string;
  openBulkAddUsers?: boolean;
  onCloseBulkAddUsers?: () => void;
}

const Users: React.FC<IUsers> = ({
  firmId,
  openBulkAddUsers = false,
  onCloseBulkAddUsers,
}) => {
  const loggedInUser = useUser();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [isResetPasswordLinkModalOpen, setIsResetPasswordLinkModalOpen] =
    useState(false);
  const [isLoadingFromNewLawFirm, setisLoadingFromNewLawFirm] =
    useState(openBulkAddUsers);
  const [isBulkAddUsersModalOpen, setIsBulkAddUsersModalOpen] =
    useState(openBulkAddUsers);
  const [selectedUser, setSelectedUser] = useState<any>({});

  useEffect(() => {
    if (openBulkAddUsers) {
      setIsBulkAddUsersModalOpen(true);
    }
  }, [openBulkAddUsers]);

  const {
    data: users,
    isFetching,
    refetch,
  } = useGetUsersQuery(`firmId=${firmId}`, {
    skip: !firmId,
  });
  const [deleteUser, { isLoading: isDeletingUser }] = useDeleteUserMutation();
  const [updateUser, { isLoading: isUpdatingUser }] = useUpdateUserMutation();
  const [createUsers, { isLoading: isCreatingUsers }] =
    useBulkCreateUserMutation();
  const mutateDelete = useMutationWrapper(
    deleteUser,
    () => `User ${selectedUser.name ?? ""} deleted successfully`,
    () => {
      refetch();
      setIsDeleteModalOpen(false);
    }
  );

  const mutateUpdate = useMutationWrapper(
    updateUser,
    (u) => `User ${u.name} updated successfully`,
    () => {
      refetch();
      setIsUpdateModalOpen(false);
    }
  );

  const mutateCreate = useMutationWrapper(
    createUsers,
    "Users will receive an email shortly with access details.",
    () => {
      refetch();
      setIsBulkAddUsersModalOpen(false);
      onCloseBulkAddUsers?.();
    },
    "Users added successfully"
  );

  const canDeleteUser = useMemo(() => {
    // if we have only one user, we cannot delete that user.
    if (users?.length === 1) return false;

    // if the user is a not a manager, then we can delete it
    if (!selectedUser?.roles?.includes("clientmanager")) return true;

    // if the user is a manager, and the only manager then we cannot delete them
    return users?.filter((u) => u.roles.includes("clientmanager")).length !== 1;
  }, [users, selectedUser]);

  const canChangeRole = useMemo(() => {
    const isCurrentUserAdmin = selectedUser?.roles?.includes("clientmanager");
    return !isCurrentUserAdmin || (isCurrentUserAdmin && canDeleteUser);
  }, [selectedUser, canDeleteUser]);

  return (
    <>
      <UsersTable
        loggedInUser={loggedInUser}
        users={users}
        isFetching={isFetching}
        onBulkAddUsers={() => setIsBulkAddUsersModalOpen(true)}
        onDeleteUser={(user) => {
          setSelectedUser(user);
          setIsDeleteModalOpen(true);
        }}
        onEditUser={(user) => {
          setSelectedUser(user);
          setIsUpdateModalOpen(true);
        }}
        onResetPasswordUser={(user) => {
          setSelectedUser(user);
          setIsResetPasswordLinkModalOpen(true);
        }}
      />
      <DeleteUserModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        selectedUser={selectedUser}
        onDelete={() => mutateDelete(selectedUser.id)}
        canDeleteUser={canDeleteUser}
        isLoading={isDeletingUser}
      />
      <BulkAddUsersModal
        isOpen={isBulkAddUsersModalOpen}
        isLoading={isCreatingUsers}
        onClose={() => {
          setIsBulkAddUsersModalOpen(false);
          setisLoadingFromNewLawFirm(false);
          onCloseBulkAddUsers?.();
        }}
        onInvite={(emails, roles) => {
          mutateCreate({ emails, roles, firmId });
        }}
        isFirstLoad={isLoadingFromNewLawFirm}
      />
      <EditUserModal
        isOpen={isUpdateModalOpen}
        isLoading={isUpdatingUser}
        onClose={() => setIsUpdateModalOpen(false)}
        onSave={(data) => {
          mutateUpdate(data);
        }}
        user={selectedUser}
        canChangeRole={canChangeRole}
      />
      <ResetUserPasswordModal
        isOpen={isResetPasswordLinkModalOpen}
        onClose={() => setIsResetPasswordLinkModalOpen(false)}
        user={selectedUser}
      />
    </>
  );
};

export default Users;
